<template>
  <div class="space-y-4">
    <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div class="flex items-center justify-between gap-4 px-4 py-3 border-b border-gray-200">
        <div>
          <div class="text-sm font-semibold text-gray-900">Saved frames</div>
          <div class="text-xs text-gray-600 mt-1">Immutable tables kept by name: uploads, query results the agent saved, and tables kept from notebooks. Saving a name again makes a new version.</div>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-xs text-gray-600">{{ frames.length }} frames</span>
          <label class="flex items-center gap-1 text-xs text-gray-600">
            <input v-model="latestOnly" type="checkbox"> Latest per name
          </label>
        </div>
      </div>
      <div class="px-4 py-3 bg-gray-10 border-b border-gray-200">
        <label for="frame-search" class="sr-only">Search saved frames</label>
        <input
          id="frame-search"
          v-model="search"
          type="search"
          placeholder="Search by name, description or source"
          class="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-dark focus:ring-offset-4"
        />
      </div>
      <div v-if="!search" class="flex items-center gap-1 px-4 py-2 border-b border-gray-100 text-xs">
        <button type="button" class="text-blue-dark hover:underline" @click="dir = ''">All frames</button>
        <template v-for="(seg, i) in crumbs" :key="i">
          <span class="text-gray-400">/</span>
          <button type="button" class="text-blue-dark hover:underline" @click="dir = crumbs.slice(0, i + 1).join('/')">{{ seg }}</button>
        </template>
      </div>
      <LoadingIndicator v-if="loading" message="Loading saved frames..." />
      <div v-else-if="shown.length || subdirs.length" class="max-h-[32rem] overflow-y-auto">
        <table class="w-full table-fixed border-collapse">
          <colgroup>
            <col class="w-[26%]" />
            <col class="w-[30%]" />
            <col class="w-[10%]" />
            <col class="w-[8%]" />
            <col class="w-[8%]" />
            <col class="w-[18%]" />
          </colgroup>
          <thead class="sticky top-0 z-10 bg-gray-25">
            <tr class="border-b border-gray-200">
              <th class="px-4 py-2 text-left text-xs font-semibold text-gray-600">Name</th>
              <th class="px-4 py-2 text-left text-xs font-semibold text-gray-600">Source</th>
              <th class="px-4 py-2 text-left text-xs font-semibold text-gray-600">Kind</th>
              <th class="px-4 py-2 text-right text-xs font-semibold text-gray-600">Rows</th>
              <th class="px-4 py-2 text-right text-xs font-semibold text-gray-600">Columns</th>
              <th class="px-4 py-2 text-left text-xs font-semibold text-gray-600">Saved</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="d in subdirs"
              :key="'dir:' + d.path"
              role="button"
              tabindex="0"
              class="border-b border-gray-50 cursor-pointer hover:bg-gray-25 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-dark"
              @click="dir = d.path"
              @keydown.enter="dir = d.path"
            >
              <td class="px-4 py-2 text-xs font-semibold text-gray-900"><font-awesome-icon :icon="['fas', 'folder']" class="w-3.5 h-3.5 text-gray-500 mr-2" />{{ d.label }}/</td>
              <td class="px-4 py-2 text-xs text-gray-500" colspan="4">{{ d.count }} frame{{ d.count === 1 ? '' : 's' }}</td>
              <td class="px-4 py-2 text-xs text-gray-500 whitespace-nowrap">{{ when(d.latest) }}</td>
            </tr>
            <template v-for="f in shown" :key="f.id">
              <tr
                role="button"
                tabindex="0"
                class="border-b border-gray-50 cursor-pointer hover:bg-gray-25 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-dark align-top"
                :class="{ 'bg-gray-25': openId === f.id }"
                @click="toggle(f)"
                @keydown.enter="toggle(f)"
                @keydown.space.prevent="toggle(f)"
              >
                <td class="sensitive px-4 py-2 text-xs font-semibold text-gray-900 whitespace-normal break-words">
                  {{ search ? f.name : leaf(f.name) }}
                  <span v-if="isSuperseded(f)" class="ml-1 text-[10px] font-medium text-gray-500 uppercase">superseded</span>
                </td>
                <td class="sensitive px-4 py-2 text-xs text-gray-600 whitespace-normal break-words" data-demo-blur>{{ sourceOf(f) }}</td>
                <td class="px-4 py-2 text-xs text-gray-600">{{ kindOf(f) }}</td>
                <td class="px-4 py-2 text-xs text-right text-gray-600 tabular-nums">{{ f.row_count.toLocaleString() }}</td>
                <td class="px-4 py-2 text-xs text-right text-gray-600 tabular-nums">{{ (f.columns || []).length }}</td>
                <td class="px-4 py-2 text-xs text-gray-600 whitespace-nowrap">{{ when(f.created_at) }}<span v-if="f.created_by_name" class="text-gray-400"> · {{ f.created_by_name }}</span></td>
              </tr>
              <tr v-if="openId === f.id" class="border-b border-gray-100">
                <td colspan="6" class="px-4 py-3 bg-gray-10">
                  <div class="flex items-center justify-between gap-3 mb-2">
                    <div class="text-xs text-gray-600">
                      <span class="font-semibold text-gray-900">Columns:</span>
                      <span class="sensitive">{{ (f.columns || []).map((c) => c.name).join(', ') }}</span>
                    </div>
                    <div class="flex items-center gap-2">
                      <router-link :to="`/impact/frames/${f.id}`" class="btn-secondary-sm text-xs" @click.stop>Open</router-link>
                      <button type="button" class="btn-secondary-sm text-xs" @click.stop="download(f)">Download CSV</button>
                    </div>
                  </div>
                  <LoadingIndicator v-if="sampleLoading" message="Loading sample..." />
                  <div v-else-if="sampleError" class="text-xs text-red-dark">{{ sampleError }}</div>
                  <div v-else-if="sample.rows && sample.rows.length" class="overflow-x-auto">
                    <table class="border-collapse text-xs">
                      <thead>
                        <tr>
                          <th v-for="c in sample.columns" :key="c.name" class="px-2 py-1 text-left font-semibold text-gray-600 border-b border-gray-200 whitespace-nowrap">{{ c.name }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(r, i) in sample.rows" :key="i" class="border-b border-gray-50">
                          <td v-for="c in sample.columns" :key="c.name" class="sensitive px-2 py-1 text-gray-800 whitespace-nowrap">{{ r[c.name] }}</td>
                        </tr>
                      </tbody>
                    </table>
                    <div class="text-[11px] text-gray-500 mt-1">First {{ sample.rows.length }} of {{ f.row_count.toLocaleString() }} rows</div>
                  </div>
                  <div v-else class="text-xs text-gray-500">No rows.</div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <div v-else class="px-4 py-10 text-center">
        <div class="text-sm text-gray-700">{{ dir || search ? 'Nothing here.' : 'No saved frames yet.' }}</div>
        <div class="text-xs text-gray-500 mt-1">Upload a table in the agent, ask it to save a query result, or keep a result from a notebook cell.</div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import { beginSensitiveEgress } from '@/utils/demoEgress';

export default {
  name: 'SavedFrameCatalog',
  components: { LoadingIndicator },
  data() {
    return {
      frames: [], loading: false, search: '', latestOnly: true, dir: '',
      openId: null, sample: { columns: [], rows: [] }, sampleLoading: false, sampleError: '',
    };
  },
  computed: {
    supersededIds() {
      return new Set(this.frames.map((f) => f.supersedes).filter(Boolean));
    },
    visible() {
      return this.frames.filter((f) => !(this.latestOnly && this.supersededIds.has(f.id)));
    },
    crumbs() {
      return this.dir ? this.dir.split('/') : [];
    },
    shown() {
      const q = this.search.trim().toLowerCase();
      if (q) {
        return this.visible.filter((f) => `${f.name} ${f.description} ${this.sourceOf(f)}`.toLowerCase().includes(q));
      }
      return this.visible.filter((f) => this.dirOf(f.name) === this.dir);
    },
    subdirs() {
      if (this.search.trim()) return [];
      const base = this.dir ? this.dir + '/' : '';
      const found = {};
      this.visible.forEach((f) => {
        if (!f.name.startsWith(base)) return;
        const rest = f.name.slice(base.length);
        if (!rest.includes('/')) return;
        const label = rest.split('/')[0];
        const path = base + label;
        const entry = found[path] || (found[path] = { path, label, count: 0, latest: '' });
        entry.count += 1;
        if (f.created_at > entry.latest) entry.latest = f.created_at;
      });
      return Object.values(found).sort((a, b) => a.label.localeCompare(b.label));
    },
  },
  mounted() {
    this.fetchFrames();
  },
  methods: {
    async fetchFrames() {
      this.loading = true;
      try {
        const { data } = await axios.get('api/impact_analytics/frames/');
        this.frames = Array.isArray(data) ? data : (data.results || []);
      } catch {
        this.frames = [];
      } finally {
        this.loading = false;
      }
    },
    isSuperseded(f) {
      return this.supersededIds.has(f.id);
    },
    dirOf(name) {
      return name.includes('/') ? name.slice(0, name.lastIndexOf('/')) : '';
    },
    leaf(name) {
      return name.includes('/') ? name.slice(name.lastIndexOf('/') + 1) : name;
    },
    kindOf(f) {
      const kind = (f.provenance || {}).kind || '';
      return { upload: 'Upload', inline: 'Agent rows', query: 'Query', sql: 'Notebook SQL', kernel: 'Notebook' }[kind] || (kind || ((f.provenance || {}).query ? 'Query' : 'Saved'));
    },
    sourceOf(f) {
      const p = f.provenance || {};
      if (p.kind === 'upload') return `${p.filename || 'uploaded file'}${p.sheet ? ` · sheet ${p.sheet}` : ''}`;
      if (p.notebook_id) return `Notebook ${p.notebook_id}${p.cell ? ` · cell ${p.cell}` : ''}`;
      if (p.query) return String(p.query).replace(/\s+/g, ' ').slice(0, 140);
      return f.description || '';
    },
    when(iso) {
      if (!iso) return '';
      return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    },
    async toggle(f) {
      if (this.openId === f.id) { this.openId = null; return; }
      this.openId = f.id;
      this.sample = { columns: f.columns || [], rows: [] };
      this.sampleError = '';
      this.sampleLoading = true;
      try {
        const { data } = await axios.get(`api/impact_analytics/frames/${f.id}/sample/`, { params: { n: 25 } });
        this.sample = { columns: data.columns || f.columns || [], rows: data.rows || [] };
      } catch (err) {
        this.sampleError = err?.response?.status === 403
          ? 'Rows are patient-level data; your role does not include PHI access.'
          : (err?.response?.data?.error || err?.response?.data?.detail || 'The sample could not be loaded.');
      } finally {
        this.sampleLoading = false;
      }
    },
    async download(f) {
      const ticket = beginSensitiveEgress('download');
      if (!ticket) return;
      try {
        const response = await axios.get(`api/impact_analytics/frames/${f.id}/download/`, { responseType: 'blob' });
        if (!ticket.confirm()) return;
        const url = URL.createObjectURL(response.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${f.name.replace(/[^a-z0-9]+/gi, '_')}_${f.id}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 2000);
      } catch (err) {
        this.sampleError = err?.response?.status === 403
          ? 'Downloading rows needs PHI access.'
          : 'The download failed.';
      }
    },
  },
};
</script>
